import { DayjsDateAdapter } from './shared/adapters/dayjs-date-adapter';
import localePt from '@angular/common/locales/pt';

import { registerLocaleData } from '@angular/common';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { GlobalErrorHandler } from './shared/exceptions/global-error-handler';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NgxMaskModule } from 'ngx-mask';

export const customTooltipOptions: MatTooltipDefaultOptions = {
  showDelay: 300,
  hideDelay: 0,
  touchendHideDelay: 1500,
  touchGestures: 'off',
};

import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';

import { AutocompleteOffDirective } from './shared/pipes/directive/auto-close-timer-directive.pipe';
import { AppRoutingModule } from './app.routing';
import { AppComponent } from './app.component';
import { PagesComponent } from './pages/pages.component';
import { AuthGuard } from './shared/guard';
import { AuthService } from './shared/services/auth.service';
import { HttpConfigInterceptor } from './shared/interceptor/httpconfig.interceptor';
import { CustomPaginator } from './theme/utils/custom-paginator';
import { ServiceWorkerModule } from '@angular/service-worker';

import { UsermenuComponent } from './shared/components/usermenu/usermenu.component';
import { ActiveadminComponent } from './shared/components/activeadmin/activeadmin.component';
import { SmileyComponent } from './shared/components/smiley-help/smiley/smiley.component';

import { FooterComponent } from './shared/components/footer/footer.component';
import { FullmenuComponent } from './shared/components/fullmenu/fullmenu.component';
import { HttpLoaderFactory } from './i18n/translate-loader.factory';
import { LanguageService } from './shared/services/language.service';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AppMissingTranslationHandler } from './i18n/missing-translation.handler';
import { SessionExpirationWarningComponent } from './shared/components/session-expiration-warning/session-expiration-warning.component';
import { NetworkStatusComponent } from './shared/components/network-status/network-status.component';
import { ApiKeyInterceptor } from './core/interceptors/api-key.interceptor';
import { ChatbotComponent } from './shared/components/chatbot/chatbot.component';
import { MY_DATE_FORMAT } from './shared/services/datetime.service';

export function initLangFactory(langSvc: LanguageService) {
  return () => langSvc.init();
}

registerLocaleData(localePt, 'pt-PT');

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useFactory: HttpLoaderFactory, deps: [HttpClient] },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: AppMissingTranslationHandler,
      },
    }),

    // ✅ .forRoot() — configuração global de serviços (obrigatório no root module)
    NgxMaskModule.forRoot(),
    ServiceWorkerModule.register('./ngsw-worker.js', {
      enabled: false,
      registrationStrategy: 'registerWhenStable:30000',
    }),

    // ✅ Standalone components usados nos templates do AppComponent / MaintenanceComponent
    AutocompleteOffDirective,
    SmileyComponent,
    FooterComponent,
    FullmenuComponent,
    UsermenuComponent,
    ActiveadminComponent,
    SessionExpirationWarningComponent,
    NetworkStatusComponent,
    PagesComponent,
    ChatbotComponent,
  ],
  declarations: [
    AppComponent,
    // PagesComponent,
    // SearchComponent,
    // MaintenanceComponent,
  ],
  providers: [
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    AuthGuard,
    AuthService,
    HttpClient,
    { provide: APP_INITIALIZER, useFactory: initLangFactory, deps: [LanguageService], multi: true },
    // Opcional: define locale base dos pipes Angular (datas, números) para PT por defeito
    { provide: LOCALE_ID, useValue: 'pt-PT' },
    // AppSettings,
    { provide: MatPaginatorIntl, useClass: CustomPaginator },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    { provide: 'Europe/Lisbon', useValue: 'UTC' },
    { provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE] },

    { provide: MAT_DATE_LOCALE, useValue: 'pt-PT' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    // Providers from MaterialModule, TranslateModule
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiKeyInterceptor,
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: (scrollStrategyOptions: ScrollStrategyOptions): MatDialogConfig => ({
        scrollStrategy: scrollStrategyOptions.noop(),
        hasBackdrop: true,
        disableClose: false,
      }),
      deps: [ScrollStrategyOptions],
    },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: customTooltipOptions },
    // { provide: LocationStrategy, useClass: HashLocationStrategy },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {}
}
